import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Moon, Loader2, Play, Pause, Sparkles } from "lucide-react";
import axios from "axios";
import StarCanvas from "@/components/StarCanvas";
import { parseStory } from "@/components/StoryGenerator";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VOICE_NAMES = { shimmer: "Luna", onyx: "Barnaby", nova: "Aria", fable: "Jasper" };

const themeLabel = (t) =>
  t ? t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Bedtime Adventure";

const formatTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

function SharedAudioPlayer({ narrations }) {
  const [selected, setSelected] = useState(narrations[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) a.pause();
    else a.play();
  };

  return (
    <div data-testid="shared-audio-player" className="glass-card rounded-2xl p-6 mt-8">
      <p className="text-xs font-mono tracking-[0.2em] uppercase text-amber-300/80 mb-4">Listen to this story</p>
      <div className="flex flex-wrap gap-2 mb-5">
        {narrations.map((n) => (
          <button
            key={n.audio_id}
            data-testid={`shared-voice-${n.voice_id}`}
            onClick={() => { setSelected(n); setIsPlaying(false); setProgress(0); setCurrentTime(0); }}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selected.audio_id === n.audio_id
                ? "bg-amber-500/20 border border-amber-400/40 text-amber-300"
                : "border border-purple-500/20 text-slate-400 hover:text-white"
            }`}
          >
            {VOICE_NAMES[n.voice_id] || n.voice_id}
          </button>
        ))}
      </div>
      <audio
        key={selected.audio_id}
        ref={audioRef}
        src={`${API}/audio/${selected.audio_id}`}
        onTimeUpdate={(e) => {
          const t = e.target.currentTime;
          const d = e.target.duration || 0;
          setCurrentTime(t);
          setProgress(d ? (t / d) * 100 : 0);
          if (d && t > 5 && t < d - 5) localStorage.setItem(`dw-resume-${selected.audio_id}`, String(t));
          else if (d && t >= d - 5) localStorage.removeItem(`dw-resume-${selected.audio_id}`);
        }}
        onLoadedMetadata={(e) => {
          setDuration(e.target.duration);
          const saved = parseFloat(localStorage.getItem(`dw-resume-${selected.audio_id}`) || "0");
          if (saved > 5 && saved < e.target.duration - 10) {
            e.target.currentTime = saved;
            setCurrentTime(saved);
            setProgress((saved / e.target.duration) * 100);
          }
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false); setProgress(0); setCurrentTime(0);
          localStorage.removeItem(`dw-resume-${selected.audio_id}`);
        }}
      />
      <div className="flex items-center gap-4">
        <button
          data-testid="shared-play-btn"
          onClick={togglePlay}
          className="w-12 h-12 shrink-0 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 flex items-center justify-center text-white shadow-lg shadow-purple-500/30"
        >
          {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <div className="flex-1">
          <input
            type="range" min={0} max={100} step={0.1}
            value={progress}
            onChange={(e) => {
              const a = audioRef.current;
              if (a && a.duration) a.currentTime = (parseFloat(e.target.value) / 100) * a.duration;
            }}
            className="audio-progress w-full"
            style={{ background: `linear-gradient(to right, #FBBF24 ${progress}%, rgba(255,255,255,0.15) 0%)` }}
          />
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SharedStory() {
  const { token } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const fetchedTokenRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (fetchedTokenRef.current === token) return;
    fetchedTokenRef.current = token;
    axios.get(`${API}/shared/${token}`)
      .then((res) => setStory(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A]">
        <Loader2 size={32} className="animate-spin text-amber-400" />
      </div>
    );
  }

  if (notFound || !story) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B091A] px-4">
        <StarCanvas />
        <div className="relative z-10 glass-card rounded-3xl p-12 max-w-md w-full text-center">
          <Moon size={36} className="text-purple-400 mx-auto mb-5" />
          <h2 className="font-serif text-2xl font-bold text-white mb-2">This link has drifted off to dreamland</h2>
          <p className="text-slate-400 text-sm mb-6">The shared story could not be found. Ask for a fresh magic link.</p>
          <Link to="/" className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm">
            Discover DreamWeaver
          </Link>
        </div>
      </div>
    );
  }

  const parts = parseStory(story.story_text);
  const ready = new Set(story.illustrations_ready || []);

  return (
    <div data-testid="shared-story-page" className="relative min-h-screen bg-[#0B091A] overflow-x-clip">
      <StarCanvas />
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 pb-24">
        {/* Header */}
        <header className="flex items-center justify-between py-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Moon size={14} className="text-white" />
            </div>
            <span className="font-serif font-bold text-lg tracking-tight gradient-text">DreamWeaver</span>
          </Link>
          <span className="text-xs font-mono tracking-[0.2em] uppercase text-amber-300/70">Shared Story</span>
        </header>

        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="mt-10 mb-10 text-center">
          <h1 data-testid="shared-story-title" className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold gradient-text leading-tight">
            {story.title}
          </h1>
          <p className="text-sm text-slate-500 mt-4">
            A bedtime story for {story.child_name}{story.sibling_name ? ` & ${story.sibling_name}` : `, age ${story.child_age}`} · {themeLabel(story.theme)}
            {story.word_count ? ` · ~${Math.ceil(story.word_count / 130)} min read` : ""}
          </p>
        </motion.div>

        {/* Narration */}
        {story.narrations && story.narrations.length > 0 && (
          <SharedAudioPlayer narrations={story.narrations} />
        )}

        {/* Story body */}
        <div className="glass-card rounded-2xl p-6 sm:p-10 mt-8">
          {parts.map((part, i) =>
            part.type === "text" ? (
              <div key={i} className="story-body">
                {part.content.split(/\n\n+/).filter(Boolean).map((p, j) => (
                  <p key={j}>{p.trim()}</p>
                ))}
              </div>
            ) : ready.has(part.index) ? (
              <div key={i} className="my-8 rounded-2xl overflow-hidden border border-purple-500/20 relative">
                <img
                  src={`${API}/stories/${story.story_id}/illustrations/${part.index}`}
                  alt={`Story illustration ${part.index + 1}`}
                  className="w-full h-80 object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-xs text-slate-300 font-serif italic">{part.prompt}</p>
                </div>
              </div>
            ) : (
              <div key={i} className="my-8 rounded-2xl border border-purple-500/20 p-10 flex flex-col items-center justify-center gap-3 text-center" style={{ background: "rgba(22,18,48,0.5)" }}>
                <Moon size={26} className="text-purple-400/60" />
                <p className="text-sm text-slate-500 font-serif italic">Illustration coming soon...</p>
              </div>
            )
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 glass-card rounded-2xl p-10 text-center">
          <Sparkles size={24} className="text-amber-400 mx-auto mb-4" />
          <h3 className="font-serif text-2xl font-bold text-white mb-2">Want a story starring your little one?</h3>
          <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
            DreamWeaver crafts magical, personalized bedtime stories with watercolor illustrations and soothing narration. Your first story is free.
          </p>
          <Link
            data-testid="create-your-own-btn"
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white font-semibold text-sm hover:shadow-lg hover:shadow-purple-500/30 transition-all"
          >
            <Sparkles size={14} /> Create Your Own Free Story
          </Link>
        </div>
      </div>
    </div>
  );
}
